import { useEffect, useState } from "react";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { Bars3BottomLeftIcon, CheckIcon, EqualsIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import usePriceSettingsMutations from "../../../data/usePriceSettingsMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import classNames from "../../../utils/classNames";
import { EngineCodeMatchType, ICustomerGenericPriceSetting } from "../../../models/PriceSettings";
import Tooltip from "../../shared/Tooltip";

// Spaces and case are always ignored. Starts with and Exact also ignore hyphens; Contains doesn't,
// so rules left on the default match exactly as they did before this setting existed.
const matchTypes = [
  { id: EngineCodeMatchType.Contains,   name: "Contains",    icon: MagnifyingGlassIcon, example: "630 matches M9R-630" },
  { id: EngineCodeMatchType.StartsWith, name: "Starts with", icon: Bars3BottomLeftIcon, example: "M9R matches M9R-630" },
  { id: EngineCodeMatchType.Exact,      name: "Exact",       icon: EqualsIcon,          example: "M9R630 matches only M9R-630" },
];

export default function EngineCodeMatchTypeButton({
  priceSetting,
}: { priceSetting: ICustomerGenericPriceSetting }) {
  const [selected, setSelected] = useState<EngineCodeMatchType>(priceSetting.engineCodeMatchType ?? EngineCodeMatchType.Contains);

  const { updateSpecific } = usePriceSettingsMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Specific Price Setting");

  useEffect(() => {
    setSelected(priceSetting.engineCodeMatchType ?? EngineCodeMatchType.Contains);
  }, [priceSetting.engineCodeMatchType]);

  const handleChange = (matchType: EngineCodeMatchType) => {
    if (matchType === selected) return;

    setSelected(matchType);

    updateSpecific.mutate({ ...priceSetting, engineCodeMatchType: matchType }, {
      onError: () => saveHandlers.onError(),
      onSuccess: () => saveHandlers.onSuccess(),
    });
  };

  const current = matchTypes.find((t) => t.id === selected) ?? matchTypes[0];
  const Icon = current.icon;

  return (
    <Listbox value={selected} onChange={handleChange}>
      <Tooltip content={`Engine code match: ${current.name}`} className="flex">
        <ListboxButton
          aria-label={`Engine code match: ${current.name}`}
          className={classNames(
            // Contains is the default, so it stays quiet; a stricter match stands out in the table.
            selected === EngineCodeMatchType.Contains
              ? "border-gray-300 text-gray-400 hover:text-gray-600"
              : "border-primary-300 bg-primary-50 text-primary-600",
            "mr-1 flex items-center rounded-md border p-1 shadow-sm"
          )}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        </ListboxButton>
      </Tooltip>

      <ListboxOptions
        anchor="bottom start"
        className="z-50 w-48 [--anchor-gap:4px] rounded-md bg-white py-1 text-xs shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
      >
        {matchTypes.map((t) => (
          <ListboxOption
            key={t.id}
            value={t.id}
            className={({ focus }: { focus: boolean }) =>
              classNames(
                focus ? "bg-primary-100" : "",
                "relative cursor-default select-none py-2 pl-8 pr-3 text-gray-900"
              )
            }
          >
            <span className={classNames(selected === t.id ? "font-semibold" : "font-normal", "block")}>
              {t.name}
            </span>
            <span className="block text-[11px] text-gray-500">{t.example}</span>
            {selected === t.id ? (
              <span className="absolute inset-y-0 left-0 flex items-center pl-1.5 text-primary-600">
                <CheckIcon className="h-5 w-5" aria-hidden="true" />
              </span>
            ) : null}
          </ListboxOption>
        ))}
      </ListboxOptions>
    </Listbox>
  );
}
